import { useRef, useState, type ReactNode } from 'react';
import { Modal, Spin, Tabs } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import {
  getTestShipmentCandidates,
  type TestShipmentCandidateRow,
} from '@/apis/logiblocksAPI';
import { getAlertDisplayName } from '@/common/alertType';
import { useToken } from '@/common/helper';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import styles from './SelectTestShipmentModal.module.css';
import {
  formatTestShipmentDisplayId,
  formatTestShipmentMetaLine,
} from './testRunUtils';

const PAST_SHIPMENTS_TAB = 'past_shipments';

export interface UseSelectTestShipmentModalArgs {
  /** Called with the shipment the user picks. The modal closes after it. */
  onSelect: (row: TestShipmentCandidateRow) => void;
}

export interface SelectTestShipmentModal {
  /** Opens the modal on the past shipments an alert trigger with `alertTypes` can replay. */
  openSelectShipmentModal: (alertTypes: string[]) => void;
  selectShipmentModal: ReactNode;
}

/**
 * Returns the Select shipment modal and a function that opens it. The modal lists the past
 * shipments an alert trigger can replay in a test run, newest first.
 */
export function useSelectTestShipmentModal({
  onSelect,
}: UseSelectTestShipmentModalArgs): SelectTestShipmentModal {
  const { getToken } = useToken();
  const { dateFormat } = useUserDateTimeFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState<TestShipmentCandidateRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Numbers each load, so a response arriving after a newer load started is dropped.
  const latestLoadIdRef = useRef(0);

  /**
   * Fetches the candidates for `alertTypes` into `rows`, or the reason they failed into
   * `loadError`. Writes nothing once a newer load than `loadId` has started.
   */
  async function loadRows(alertTypes: string[], loadId: number): Promise<void> {
    const token = getToken();
    if (token === null) {
      console.error('Cannot list test shipments: no auth token');
      setLoadError('You are signed out. Sign in and try again.');
      return;
    }
    setIsLoading(true);
    try {
      const candidates = await getTestShipmentCandidates(token, alertTypes);
      if (loadId === latestLoadIdRef.current) {
        setRows(candidates);
      }
    } catch (error) {
      console.error('Failed to list test shipments:', error);
      if (loadId === latestLoadIdRef.current) {
        setLoadError(error instanceof Error ? error.message : String(error));
      }
    } finally {
      if (loadId === latestLoadIdRef.current) {
        setIsLoading(false);
      }
    }
  }

  /** Opens the modal and starts loading the candidates for `alertTypes` into it. */
  function openSelectShipmentModal(alertTypes: string[]): void {
    latestLoadIdRef.current += 1;
    setIsOpen(true);
    setRows([]);
    setLoadError(null);
    const _ = loadRows(alertTypes, latestLoadIdRef.current);
  }

  /** Reports the picked shipment and closes the modal. */
  function selectRow(row: TestShipmentCandidateRow): void {
    onSelect(row);
    setIsOpen(false);
  }

  /** Returns the modal body for the current load state. */
  function renderBody(): ReactNode {
    if (isLoading) {
      return (
        <div className={styles.message}>
          <Spin />
        </div>
      );
    }
    if (loadError !== null) {
      return (
        <div className={styles.message} role='alert'>
          {loadError}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className={styles.message}>
          No shipments have an alert this trigger replays.
        </div>
      );
    }
    return (
      <>
        <div className={styles.note}>
          Recent shipments with an alert of a type this trigger passes. The
          trigger&apos;s other filters aren&apos;t applied.
        </div>
        <div className={styles.list}>
          {rows.map((row) => (
            <button
              key={row.shipment_id}
              type='button'
              className={styles.card}
              onClick={() => selectRow(row)}
            >
              <div className={styles.cardInfo}>
                <div className={styles.titleRow}>
                  <span className={styles.displayId}>
                    {formatTestShipmentDisplayId(row)}
                  </span>
                  {row.status !== null && (
                    <span className={styles.pill}>{row.status}</span>
                  )}
                  {row.product_names.length > 0 && (
                    <span className={styles.pill}>
                      {row.product_names.length > 1
                        ? `${row.product_names[0]} +${row.product_names.length - 1}`
                        : row.product_names[0]}
                    </span>
                  )}
                  <span className={`${styles.pill} ${styles.alertTypePill}`}>
                    {getAlertDisplayName(row.alert_type, row.alert_type)}
                  </span>
                </div>
                <span className={styles.metaLine}>
                  {formatTestShipmentMetaLine(row, dateFormat)}
                </span>
              </div>
              <RightOutlined className={styles.chevron} />
            </button>
          ))}
        </div>
      </>
    );
  }

  const selectShipmentModal = (
    <Modal
      open={isOpen}
      title='Select shipment'
      onCancel={() => setIsOpen(false)}
      footer={null}
      width={664}
    >
      <Tabs
        activeKey={PAST_SHIPMENTS_TAB}
        items={[
          {
            key: PAST_SHIPMENTS_TAB,
            label: 'Past shipments',
            children: renderBody(),
          },
        ]}
      />
    </Modal>
  );

  return { openSelectShipmentModal, selectShipmentModal };
}
